import { UniqueIdentifier } from "@dnd-kit/core";
import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ClaimSystemView } from "@redotech/redo-return-operations-common/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  CLAIMS_ICON,
  CLAIMS_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
} from "../../../../redo-suite/order-management-cloud";
import { ClaimViewOrderingContext } from "../../../../services/claims/claims-view-ordering-service";
import { OrdersReviewCountContext } from "../../../../services/orders/orders-review-count-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const ClaimsItem = memo(function ClaimsItem() {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled, packageProtectionEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const isManagedClaimsAdmin = useIsRedoAdmin();

  const showPPPlusClaimsTables =
    team?.settings.packageProtection?.newClaimsTables &&
    team.settings.packageProtection.packageProtectionPlusEnabled;

  const isReturnEnabled = team?.settings.returns?.enabled;

  const canManagePackageProtectionClaims = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
  );
  const canManageCarrierClaims = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
  );
  const { roClaimsNeedingReview } = useRequiredContext(
    OrdersReviewCountContext,
  );

  const claimViewOrderingContext = useRequiredContext(ClaimViewOrderingContext);

  const allViews = useMemo(() => {
    return [...claimViewOrderingContext.views].filter(
      (view) =>
        view.name !== ClaimSystemView.CREATE &&
        view.name !== ClaimSystemView.MANAGED,
    );
  }, [claimViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...claimViewOrderingContext.folders];
  }, [claimViewOrderingContext]);

  const notificationCounts = claimViewOrderingContext.notificationCounts;

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"claims">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: routes.claimsHref,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    routes.claimsHref,
    notificationCounts,
    folderNotificationCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    claimViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"claims">(
      flattenedItems,
      allViews,
      allFolders,
    );
    claimViewOrderingContext.updateOrdering(views, folders);
  });

  const anyChildSelected = useMatch(`${routes.claimsHref}/*`);

  if (!omsEnabled && !packageProtectionEnabled && !isReturnEnabled) {
    return null;
  }

  if (!canManagePackageProtectionClaims && !canManageCarrierClaims) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.claimsDefaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={CLAIMS_ICON_FILLED}
      friendlyName="Claims"
      id="claims"
      notificationCount={totalNotificationCount}
      UnfilledIcon={CLAIMS_ICON}
    >
      <AllClaimsItem
        hidden={!(packageProtectionEnabled && canManagePackageProtectionClaims)}
      />
      <ClaimsCarrierClaimsItem
        hidden={!((omsEnabled || isReturnEnabled) && canManageCarrierClaims)}
      />
      <ManagedClaimsItem
        hidden={
          !(
            showPPPlusClaimsTables &&
            canManagePackageProtectionClaims &&
            isManagedClaimsAdmin
          )
        }
        notificationCount={roClaimsNeedingReview}
      />
      {packageProtectionEnabled && canManagePackageProtectionClaims && (
        <SortableTree
          items={sortableItems}
          onRequestEdit={handleRequestEdit}
          setItems={handleUpdateOrdering}
        />
      )}
      <ClaimsAddViewItem
        hidden={!(packageProtectionEnabled && canManagePackageProtectionClaims)}
      />
      <ClaimsCreateFolderItem
        hidden={!(packageProtectionEnabled && canManagePackageProtectionClaims)}
      />
    </RedoSecondaryNavItem>
  );
});

const AllClaimsItem = memo(function AllClaimsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getClaimsSubRoute("all") }}
      friendlyName="All claims"
      hidden={hidden}
      id="all-claims"
    />
  );
});

const ClaimsCarrierClaimsItem = memo(function ClaimsCarrierClaimsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getClaimsSubRoute("carrier-claims"),
      }}
      friendlyName="Carrier claims"
      hidden={hidden}
      id="carrier-claims"
    />
  );
});

const ManagedClaimsItem = memo(function ManagedClaimsItem({
  hidden,
  notificationCount,
}: {
  hidden: boolean;
  notificationCount?: number;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getClaimsSubRoute("managed") }}
      friendlyName="Managed"
      hidden={hidden}
      id="managed-claims"
      notificationCount={notificationCount}
    />
  );
});

const ClaimsAddViewItem = memo(function ClaimsAddViewItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getClaimsSubRoute("create") }}
      friendlyName="+ Add new view"
      hidden={hidden}
      id="orders-claims-create"
    />
  );
});

const ClaimsCreateFolderItem = memo(function ClaimsCreateFolderItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const claimViewOrderingContext = useRequiredContext(ClaimViewOrderingContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          claimViewOrderingContext.openCreateFolderModal();
        },
      }}
      friendlyName="+ Add new folder"
      hidden={hidden}
      id="orders-claims-create-folder"
    />
  );
});
